import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Button,
  Divider,
  Paper,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField,
  Modal,
  Breadcrumbs,
  Link
} from '@mui/material';
import { 
  Person, 
  Email, 
  CalendarToday, 
  Security, 
  Badge,
  PowerSettingsNew,
  CheckCircle,
  Cancel,
  Send,
  LockReset,
  MoreVert,
  Delete
} from '@mui/icons-material';
import apiService from '../../services/apiService';
import UserCompaniesAndOrganizationsCard from '../cards/UserCompaniesAndOrganizationsCard';
import UserFeatureFlagsCard from '../cards/UserFeatureFlagsCard';
import UserCronJobsCard from '../cards/UserCronJobsCard';
import ReminderEmailsCard from '../cards/ReminderEmailsCard';
import { useToast } from '../common/ToastProvider';
import '../styles/ContentComponents.css';

// Add CSS animation for pulse effect
const pulseAnimation = `
  @keyframes pulse {
    0% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
    100% {
      opacity: 1;
    }
  }
`;

// Inject the CSS
const style = document.createElement('style');
style.textContent = pulseAnimation;
document.head.appendChild(style);

function UserDetail() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activationLoading, setActivationLoading] = useState(false);
  const [sendEmailLoading, setSendEmailLoading] = useState(false);
  const [sendResetPasswordLoading, setSendResetPasswordLoading] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState({
    open: false,
    newActivated: null
  });
  const [emailConfirmationModal, setEmailConfirmationModal] = useState({
    open: false
  });
  const [resetPasswordConfirmationModal, setResetPasswordConfirmationModal] = useState({
    open: false
  });
  const [userOptionsModalOpen, setUserOptionsModalOpen] = useState(false);
  const [deleteUserModalOpen, setDeleteUserModalOpen] = useState(false);
  const [deleteUserLoading, setDeleteUserLoading] = useState(false);
  const [deleteUserConfirmation, setDeleteUserConfirmation] = useState('');

  useEffect(() => {
    fetchUserDetail();
  }, [userId]);

  const fetchUserDetail = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await apiService.fetchUserById(userId);
      
      if (result.success) {
        setUser(result.data.user);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to load user details');
    }
    
    setLoading(false);
  };



  const handleActivationChange = (newActivated) => {
    setConfirmationModal({
      open: true,
      newActivated
    });
  };

  const handleConfirmActivation = async () => {
    if (!user || confirmationModal.newActivated === null) return;
    
    setActivationLoading(true);
    setConfirmationModal({ open: false, newActivated: null });
    
    try {
      const result = await apiService.updateUserActivation(userId, confirmationModal.newActivated);
      
      if (result.success) {
        // Update the local user state
        setUser(prev => ({
          ...prev,
          activated: confirmationModal.newActivated
        }));
        
        showSuccess(
          `User ${confirmationModal.newActivated ? 'activated' : 'deactivated'} successfully`,
          'Activation Updated'
        );
      } else {
        showError(result.error, 'Activation Update Failed');
      }
    } catch (err) {
      showError('Failed to update user activation status', 'Error');
      console.error('Error updating user activation:', err);
    }
    
    setActivationLoading(false);
  };

  const handleCancelActivation = () => {
    setConfirmationModal({ open: false, newActivated: null });
  };

  const handleSendActivationEmail = () => {
    setEmailConfirmationModal({ open: true });
  };

  const handleConfirmSendEmail = async () => {
    if (!user) return;
    
    setEmailConfirmationModal({ open: false });
    setSendEmailLoading(true);
    
    try {
      const result = await apiService.sendActivationEmail(userId);
      
      if (result.success) {
        showSuccess(
          `Activation email sent successfully to ${user.user_name}`,
          'Email Sent'
        );
      } else {
        showError(result.error, 'Failed to Send Email');
      }
    } catch (err) {
      showError('Failed to send activation email', 'Error');
      console.error('Error sending activation email:', err);
    }
    
    setSendEmailLoading(false);
  };

  const handleCancelSendEmail = () => {
    setEmailConfirmationModal({ open: false });
  };

  const handleSendResetPasswordEmail = () => {
    setResetPasswordConfirmationModal({ open: true });
  };

  const handleConfirmSendResetPasswordEmail = async () => {
    if (!user) return;
    
    setResetPasswordConfirmationModal({ open: false });
    setSendResetPasswordLoading(true);
    
    try {
      const result = await apiService.sendResetPasswordEmail(userId);
      
      if (result.success) {
        showSuccess(
          `Reset password email sent successfully to ${user.user_name}`,
          'Reset Password Email Sent'
        );
      } else {
        showError(result.error, 'Failed to Send Reset Password Email');
      }
    } catch (err) {
      showError('Failed to send reset password email', 'Error');
      console.error('Error sending reset password email:', err);
    }
    
    setSendResetPasswordLoading(false);
  };

  const handleCancelSendResetPasswordEmail = () => {
    setResetPasswordConfirmationModal({ open: false });
  };

  const handleUserOptionsClick = () => {
    setUserOptionsModalOpen(true);
  };

  const handleUserOptionsModalClose = () => {
    setUserOptionsModalOpen(false);
  };

  const handleDeleteUserClick = () => {
    setUserOptionsModalOpen(false);
    setDeleteUserConfirmation('');
    setDeleteUserModalOpen(true);
  };

  const handleDeleteUserModalClose = () => {
    setDeleteUserModalOpen(false);
    setDeleteUserConfirmation('');
  };

  const handleDeleteUserConfirm = async () => {
    if (deleteUserConfirmation !== user.user_name) {
      showError('Please enter the correct username to confirm deletion', 'Validation Error');
      return;
    }

    setDeleteUserLoading(true);

    try {
      const result = await apiService.deleteUser(userId);

      if (result.success) {
        console.log('User deleted successfully:', result.data);
        showSuccess(
          `User '${user.first_name} ${user.last_name}' and all related data deleted successfully`,
          'User Deleted'
        );
        // Navigate back to users list
        navigate('/users');
      } else {
        console.error('Failed to delete user:', result.error);
        showError(`Failed to delete user: ${result.error}`, 'Error');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      showError('Failed to delete user. Please try again.', 'Error');
    } finally {
      setDeleteUserLoading(false);
    }
  };

  const formatToken = (token) => {
    if (!token) return 'No token';
    return token;
  };

  const getActivationStatus = (activated) => {
    return activated ? 'Activated' : 'Not Activated';
  };

  const getOptInStatus = (optIn) => {
    return optIn ? 'Opted In' : 'Not Opted In';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Activated':
      case 'Opted In':
        return 'success';
      case 'Not Activated':
      case 'Not Opted In':
        return 'warning';
      default:
        return 'default';
    }
  };



  if (loading) {
    return (
      <Box sx={{ 
        width: '100%', 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px' 
      }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ 
        width: '100%', 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center',
        gap: 3,
        px: 2
      }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!user) {
    return (
      <Box sx={{ 
        width: '100%', 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center',
        gap: 3,
        px: 2
      }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          User not found
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100vw', minHeight: '100vh', bgcolor: 'background.default', py: 4 }}>
      <Box sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 1, sm: 2 }, width: '100%' }}>
        {/* Breadcrumb Navigation */}
        <Breadcrumbs sx={{ mb: 3, color: 'text.secondary' }}>
          <Link 
            href="/users" 
            color="inherit" 
            underline="hover"
            sx={{ 
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5
            }}
            onClick={(e) => {
              e.preventDefault();
              navigate('/users');
            }}
          >
            <Person sx={{ fontSize: 16 }} />
            Users
          </Link>
          <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Person sx={{ fontSize: 16 }} />
            {user?.user_name || 'Unnamed User'}
          </Typography>
        </Breadcrumbs>
        
        {/* Page Header with Actions */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start', 
          mb: 4,
          flexWrap: { xs: 'wrap', md: 'nowrap' },
          gap: 2
        }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h4" fontWeight={700} gutterBottom sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              <Person />
              {user?.user_name || 'Unnamed User'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1 
            }}>
              <Typography component="span" fontWeight={500}>User ID:</Typography>
              <Typography 
                component="span" 
                sx={{ 
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                  backgroundColor: 'grey.100',
                  px: 1,
                  py: 0.25,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'grey.300'
                }}
              >
                {user?.unique_id || 'N/A'}
              </Typography>
            </Typography>
          </Box>
          
          <Box sx={{ 
            display: 'flex', 
            gap: 1, 
            flexShrink: 0,
            flexDirection: { xs: 'row', sm: 'row' }
          }}>
            <Button 
              variant="outlined" 
              startIcon={<MoreVert />}
              onClick={handleUserOptionsClick}
              sx={{ 
                minWidth: 'auto',
                px: 2
              }}
            >
              Options
            </Button>
          </Box>
        </Box>

        {/* Overview & Status Section */}
        <Card sx={{ mb: 4, width: '100%' }}>
          <CardContent>
            <Grid container spacing={3} justifyContent="center" sx={{ maxWidth: 900, mx: 'auto' }}>
              <Grid item xs={12} md={6}>
                {/* User Overview Card */}
                <Card sx={{ height: '100%', bgcolor: 'background.default', boxShadow: 0, border: '1px solid', borderColor: 'divider' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom fontWeight={600}>
                      👤 User Overview
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box>
                        <Typography variant="body2" color="text.secondary">Username</Typography>
                        <Typography variant="body1" fontWeight={600}>{user.user_name || 'No username'}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">First Name</Typography>
                        <Typography variant="body1" fontWeight={600}>{user.first_name || 'Not provided'}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">Last Name</Typography>
                        <Typography variant="body1" fontWeight={600}>{user.last_name || 'Not provided'}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">User ID</Typography>
                        <Typography variant="body1" fontWeight={600} sx={{ wordBreak: 'break-all' }}>{user.unique_id}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">Created Date</Typography>
                        <Typography variant="body1" fontWeight={600}>{user.create_date ? apiService.formatDate(user.create_date) : 'N/A'}</Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6}>
                {/* User Status Card */}
                <Card sx={{ height: '100%', bgcolor: 'background.default', boxShadow: 0, border: '1px solid', borderColor: 'divider' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom fontWeight={600}>
                      📊 User Status
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box>
                        <Typography variant="body2" color="text.secondary">Activation Status</Typography>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={user.activated}
                              onChange={(e) => handleActivationChange(e.target.checked)}
                              disabled={activationLoading}
                              color={user.activated ? "success" : "error"}
                              size="medium"
                            />
                          }
                          label={
                            <Typography variant="body1" fontWeight="500" sx={{ ml: 1 }}>
                              {getActivationStatus(user.activated)}
                            </Typography>
                          }
                        />
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">Email Opt-in Status</Typography>
                        <Box sx={{ mt: 0.5, display: 'flex', justifyContent: 'center' }}>
                          <Chip 
                            label={getOptInStatus(user.opt_in)} 
                            color={user.opt_in ? "success" : "default"} 
                            size="small" 
                            variant="filled"
                            sx={{ fontWeight: 600 }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* User Companies & Projects Section */}
        <Card sx={{ mb: 4, width: '100%' }}>
          <CardContent>
            <UserCompaniesAndOrganizationsCard userId={userId} />
          </CardContent>
        </Card>

        {/* User Feature Flags Section */}
        <Card sx={{ mb: 4, width: '100%' }}>
          <CardContent>
            <UserFeatureFlagsCard userId={userId} />
          </CardContent>
        </Card>

        {/* User Cron Jobs Section */}
        <Card sx={{ mb: 4, width: '100%' }}>
          <CardContent>
            <UserCronJobsCard userId={userId} />
          </CardContent>
        </Card>

        {/* Reminder Emails Section */}
        <Card sx={{ mb: 4, width: '100%' }}>
          <CardContent>
            <ReminderEmailsCard userId={userId} />
          </CardContent>
        </Card>

        {/* Modals remain unchanged */}
        {/* Confirmation Modal */}
        <Dialog
          open={confirmationModal.open}
          onClose={handleCancelActivation}
          aria-labelledby="activation-dialog-title"
          aria-describedby="activation-dialog-description"
        >
          <DialogTitle id="activation-dialog-title">
            {confirmationModal.newActivated ? 'Activate User' : 'Deactivate User'}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="activation-dialog-description">
              Are you sure you want to {confirmationModal.newActivated ? 'activate' : 'deactivate'} this user?
              {confirmationModal.newActivated ? 
                ' The user will be able to access the system.' : 
                ' The user will lose access to the system.'
              }
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelActivation} color="primary">
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmActivation} 
              color="primary" 
              variant="contained"
              disabled={activationLoading}
              startIcon={activationLoading ? <CircularProgress size={16} /> : null}
            >
              {activationLoading ? 'Updating...' : 'Confirm'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Email Confirmation Modal */}
        <Dialog
          open={emailConfirmationModal.open}
          onClose={handleCancelSendEmail}
          aria-labelledby="email-dialog-title"
          aria-describedby="email-dialog-description"
        >
          <DialogTitle id="email-dialog-title">
            Send Activation Email
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="email-dialog-description">
              Are you sure you want to send an activation email to <strong>{user?.user_name}</strong>?
              <br /><br />
              This will send an email to the user allowing them to activate their account.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelSendEmail} color="primary">
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmSendEmail} 
              color="primary" 
              variant="contained"
              disabled={sendEmailLoading}
              startIcon={sendEmailLoading ? <CircularProgress size={16} /> : null}
            >
              {sendEmailLoading ? 'Sending...' : 'Send Email'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Reset Password Confirmation Modal */}
        <Dialog
          open={resetPasswordConfirmationModal.open}
          onClose={handleCancelSendResetPasswordEmail}
          aria-labelledby="reset-password-dialog-title"
          aria-describedby="reset-password-dialog-description"
        >
          <DialogTitle id="reset-password-dialog-title">
            Send Reset Password Email
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="reset-password-dialog-description">
              Are you sure you want to send a reset password email to <strong>{user?.user_name}</strong>?
              <br /><br />
              This will send an email to the user allowing them to reset their password.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelSendResetPasswordEmail} color="primary">
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmSendResetPasswordEmail} 
              color="secondary" 
              variant="contained"
              disabled={sendResetPasswordLoading}
              startIcon={sendResetPasswordLoading ? <CircularProgress size={16} /> : null}
            >
              {sendResetPasswordLoading ? 'Sending...' : 'Send Reset Password Email'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* User Options Modal */}
        <Modal
          open={userOptionsModalOpen}
          onClose={handleUserOptionsModalClose}
          aria-labelledby="user-options-modal"
          aria-describedby="user-options-menu"
        >
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 300,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 0,
            overflow: 'hidden'
          }}>
            <Box sx={{ 
              p: 2, 
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: 'grey.50'
            }}>
              <Typography variant="h6">
                User Options
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {user?.user_name}
              </Typography>
            </Box>
            
            <Box sx={{ p: 1 }}>
              <Button
                fullWidth
                variant="text"
                color="error"
                startIcon={<Delete />}
                onClick={handleDeleteUserClick}
                sx={{
                  justifyContent: 'flex-start',
                  px: 2,
                  py: 1.5,
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: 'error.light',
                    color: 'error.contrastText'
                  }
                }}
              >
                Delete User
              </Button>
            </Box>
          </Box>
        </Modal>

        {/* Delete User Confirmation Modal */}
        <Modal
          open={deleteUserModalOpen}
          onClose={handleDeleteUserModalClose}
          aria-labelledby="delete-user-modal"
          aria-describedby="delete-user-confirmation"
        >
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 500,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
          }}>
            <Typography variant="h6" gutterBottom color="error">
              🗑️ Delete User
            </Typography>
            
            <Box sx={{ mt: 3 }}>
              <Alert severity="error" sx={{ mb: 3 }}>
                <Typography variant="body1" fontWeight="medium">
                  This action cannot be undone!
                </Typography>
                <Typography variant="body2">
                  Deleting this user will permanently remove all related data including:
                </Typography>
                <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                  <li>All user project roles</li>
                  <li>All user company roles</li>
                  <li>All user settings</li>
                  <li>All chat favourites</li>
                  <li>All conversations and related data</li>
                  <li>All dashboard plots</li>
                  <li>All user feature flags</li>
                  <li>The user account itself</li>
                </Box>
              </Alert>

              <Box sx={{ mb: 3 }}>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  To confirm deletion, please enter the exact username:
                </Typography>
                <Box sx={{ 
                  p: 2, 
                  bgcolor: 'grey.50', 
                  borderRadius: 1, 
                  border: '1px solid',
                  borderColor: 'grey.300',
                  mb: 2
                }}>
                  <Typography variant="body1" fontWeight="medium" color="error">
                    {user?.user_name}
                  </Typography>
                </Box>
                
                <TextField
                  fullWidth
                  label="Enter username to confirm"
                  value={deleteUserConfirmation}
                  onChange={(e) => setDeleteUserConfirmation(e.target.value)}
                  placeholder={`Type "${user?.user_name}" to confirm`}
                  disabled={deleteUserLoading}
                  error={deleteUserConfirmation !== '' && deleteUserConfirmation !== user?.user_name}
                  helperText={
                    deleteUserConfirmation !== '' && deleteUserConfirmation !== user?.user_name
                      ? 'Username does not match'
                      : ''
                  }
                  sx={{ mt: 1 }}
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button 
                  variant="outlined" 
                  onClick={handleDeleteUserModalClose}
                  disabled={deleteUserLoading}
                >
                  Cancel
                </Button>
                <Button 
                  variant="contained" 
                  color="error"
                  onClick={handleDeleteUserConfirm}
                  disabled={deleteUserLoading || deleteUserConfirmation !== user?.user_name}
                  sx={{ minWidth: 100 }}
                >
                  {deleteUserLoading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} color="inherit" />
                      Deleting...
                    </Box>
                  ) : (
                    'Delete User'
                  )}
                </Button>
              </Box>
            </Box>
          </Box>
        </Modal>
      </Box>
    </Box>
  );
}

export default UserDetail; 